import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell
} from 'recharts';
import { 
  FlaskConical, TrendingUp, TrendingDown, Play, Pause, Check, AlertTriangle,
  Users, MousePointer, Target, Award, RefreshCw, Eye, Zap, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CHART_COLORS = ['#059669', '#0EA5E9', '#F59E0B', '#E11D48'];

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  running: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  paused: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-blue-900/30 dark:text-blue-400',
};

const ABTesting = () => {
  const { shouldShowData } = useDemoData();

  const { user, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [experiments, setExperiments] = useState([]);
  const [selectedExperiment, setSelectedExperiment] = useState(null);
  const [results, setResults] = useState(null);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [period, setPeriod] = useState(30);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    fetchExperiments();
  }, []);

  useEffect(() => {
    if (selectedExperiment) {
      fetchResults(selectedExperiment.id);
    }
  }, [selectedExperiment, period]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchExperiments = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/ab-testing/experiments`, { 
        headers: getAuthHeaders() 
      });
      setExperiments(res.data.experiments || []);
      if (res.data.experiments?.length > 0 && !selectedExperiment) {
        setSelectedExperiment(res.data.experiments[0]);
      }
    } catch (error) {
      console.error('Failed to fetch experiments:', error);
      toast.error('Failed to load experiments');
    } finally {
      setLoading(false);
    }
  };

  const fetchResults = async (experimentId) => {
    setResultsLoading(true);
    try {
      const res = await axios.get(
        `${API_URL}/api/ab-testing/results/${experimentId}?days=${period}`, 
        { headers: getAuthHeaders() }
      );
      setResults(res.data);
    } catch (error) {
      console.error('Failed to fetch results:', error);
    } finally {
      setResultsLoading(false);
    }
  };

  const updateStatus = async (experimentId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.post(
        `${API_URL}/api/ab-testing/experiments/${experimentId}/status?status=${newStatus}`,
        null,
        { headers: getAuthHeaders() }
      );
      toast.success(`Experiment ${newStatus === 'running' ? 'started' : newStatus}`);
      fetchExperiments();
    } catch (error) {
      toast.error('Failed to update experiment status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (!shouldShowData) {
    return <EmptyDataState title="A/B Testing" description="Connect your cloud account to see real experiments here." />;
  }

  if (!isAdmin) {
    return (
      <div className="p-6" data-testid="ab-testing-page">
        <Card className="border-red-200 bg-red-50 dark:bg-red-900/20 dark:border-red-800">
          <CardContent className="py-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-red-700 dark:text-red-400">Access Denied</h2>
            <p className="text-red-600 dark:text-red-300 mt-2">A/B Testing is only available to administrators.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="ab-testing-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <FlaskConical className="w-6 h-6 text-emerald-500" />
            A/B Testing
          </h1>
          <p className="text-slate-400 dark:text-slate-400 mt-1">
            Optimize conversion with data-driven experiments
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period.toString()} onValueChange={(v) => setPeriod(parseInt(v))}>
            <SelectTrigger className="w-32" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => selectedExperiment && fetchResults(selectedExperiment.id)}
            disabled={resultsLoading}
            data-testid="refresh-results-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${resultsLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Experiments List */}
        <Card className="lg:col-span-1" data-testid="experiments-list-card">
          <CardHeader>
            <CardTitle className="text-lg">Experiments</CardTitle>
            <CardDescription>Active and completed tests</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            ) : (
              <div className="space-y-2">
                {experiments.map((exp) => (
                  <button
                    key={exp.id}
                    onClick={() => setSelectedExperiment(exp)}
                    className={`w-full p-3 rounded-lg text-left transition-all ${
                      selectedExperiment?.id === exp.id
                        ? 'bg-emerald-50 dark:bg-cyan-900/20 border-2 border-emerald-500'
                        : 'bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 border-2 border-transparent'
                    }`}
                    data-testid={`experiment-${exp.key}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm text-slate-900 dark:text-white truncate">
                        {exp.name}
                      </span>
                      <Badge className={`text-xs ${STATUS_COLORS[exp.status]}`}>
                        {exp.status}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-400 truncate">{exp.description}</p>
                    <p className="text-xs text-slate-400 mt-1">
                      {exp.variants?.length} variants
                    </p>
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {selectedExperiment && (
            <>
              {/* Experiment Details */}
              <Card data-testid="experiment-details-card">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {selectedExperiment.name}
                        <Badge className={STATUS_COLORS[selectedExperiment.status]}>
                          {selectedExperiment.status}
                        </Badge>
                      </CardTitle>
                      <CardDescription className="mt-1">
                        {selectedExperiment.description}
                      </CardDescription>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedExperiment.status === 'draft' && (
                        <Button
                          onClick={() => updateStatus(selectedExperiment.id, 'running')}
                          disabled={updatingStatus}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="start-experiment-btn"
                        >
                          <Play className="w-4 h-4 mr-2" />
                          Start
                        </Button>
                      )}
                      {selectedExperiment.status === 'running' && (
                        <>
                          <Button
                            variant="outline"
                            onClick={() => updateStatus(selectedExperiment.id, 'paused')}
                            disabled={updatingStatus}
                            data-testid="pause-experiment-btn"
                          >
                            <Pause className="w-4 h-4 mr-2" />
                            Pause
                          </Button>
                          <Button
                            onClick={() => updateStatus(selectedExperiment.id, 'completed')}
                            disabled={updatingStatus}
                            data-testid="complete-experiment-btn"
                          >
                            <Check className="w-4 h-4 mr-2" />
                            Complete
                          </Button>
                        </>
                      )}
                      {selectedExperiment.status === 'paused' && (
                        <Button
                          onClick={() => updateStatus(selectedExperiment.id, 'running')}
                          disabled={updatingStatus}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="resume-experiment-btn"
                        >
                          <Play className="w-4 h-4 mr-2" />
                          Resume
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Variants */}
                  <div className="mb-6">
                    <h3 className="font-semibold text-slate-900 dark:text-white mb-3">
                      Variants
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {selectedExperiment.variants?.map((variant, idx) => (
                        <div
                          key={variant.id}
                          className="p-4 rounded-lg border border-slate-200 dark:border-slate-700"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-slate-900 dark:text-white">
                              {variant.name}
                            </span>
                            <Badge variant="outline">{variant.weight}%</Badge>
                          </div>
                          <p className="text-sm text-slate-400 dark:text-slate-400">
                            {typeof variant.value === 'object' 
                              ? JSON.stringify(variant.value)
                              : variant.value
                            }
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Target Metric */}
                  <div className="flex items-center gap-2 text-sm text-slate-400 dark:text-slate-400">
                    <Target className="w-4 h-4" />
                    <span>Target Metric:</span>
                    <Badge variant="outline">{selectedExperiment.target_metric}</Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Results */}
              <Card data-testid="experiment-results-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-500" />
                    Results
                  </CardTitle>
                  <CardDescription>Performance comparison across variants</CardDescription>
                </CardHeader>
                <CardContent>
                  {resultsLoading ? (
                    <div className="space-y-4">
                      <Skeleton className="h-48 w-full" />
                      <Skeleton className="h-24 w-full" />
                    </div>
                  ) : results ? (
                    <div className="space-y-6">
                      {/* Summary Stats */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {results.results?.map((variant, idx) => (
                          <div
                            key={variant.variant_id}
                            className={`p-4 rounded-xl ${
                              results.winner?.variant_id === variant.variant_id
                                ? 'bg-emerald-50 dark:bg-emerald-900/20 border-2 border-emerald-500'
                                : 'bg-slate-50 dark:bg-slate-800'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-sm text-slate-900 dark:text-white">
                                {variant.variant_name}
                              </span>
                              {results.winner?.variant_id === variant.variant_id && (
                                <Award className="w-4 h-4 text-emerald-500" />
                              )}
                            </div>
                            <p className="text-2xl font-bold text-slate-900 dark:text-white">
                              {variant.conversion_rate}%
                            </p>
                            <p className="text-xs text-slate-400">conversion rate</p>
                            <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-slate-400">Impressions</span>
                                <span className="font-medium">{variant.impressions.toLocaleString()}</span>
                              </div>
                              <div className="flex items-center justify-between text-xs mt-1">
                                <span className="text-slate-400">Clicks</span>
                                <span className="font-medium">{variant.clicks.toLocaleString()}</span>
                              </div>
                              <div className="flex items-center justify-between text-xs mt-1">
                                <span className="text-slate-400">Conversions</span>
                                <span className="font-medium">{variant.conversions.toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Conversion Rate Chart */}
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                          <BarChart data={results.results}>
                            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                            <XAxis dataKey="variant_name" tick={{ fontSize: 12 }} />
                            <YAxis tick={{ fontSize: 12 }} unit="%" />
                            <Tooltip 
                              formatter={(value) => [`${value}%`, 'Conversion Rate']}
                              contentStyle={{
                                backgroundColor: 'rgba(255,255,255,0.95)',
                                borderRadius: '8px',
                                border: 'none',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                              }}
                            />
                            <Bar dataKey="conversion_rate" radius={[4, 4, 0, 0]}>
                              {results.results?.map((entry, index) => (
                                <Cell 
                                  key={`cell-${index}`} 
                                  fill={results.winner?.variant_id === entry.variant_id 
                                    ? '#059669' 
                                    : CHART_COLORS[index % CHART_COLORS.length]
                                  } 
                                />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      </div>

                      {/* Statistical Significance */}
                      {results.significance && Object.keys(results.significance).length > 0 && (
                        <div>
                          <h3 className="font-semibold text-slate-900 dark:text-white mb-3">
                            Statistical Significance vs Control
                          </h3>
                          <div className="space-y-2">
                            {Object.entries(results.significance).map(([variantId, data]) => (
                              <div
                                key={variantId}
                                className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800"
                              >
                                <div className="flex items-center gap-3">
                                  <span className="font-medium text-slate-900 dark:text-white">
                                    {results.results?.find(r => r.variant_id === variantId)?.variant_name}
                                  </span>
                                  <Badge
                                    className={
                                      data.lift > 0
                                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                    }
                                  >
                                    {data.lift > 0 ? (
                                      <TrendingUp className="w-3 h-3 mr-1" />
                                    ) : (
                                      <TrendingDown className="w-3 h-3 mr-1" />
                                    )}
                                    {data.lift > 0 ? '+' : ''}{data.lift}% lift
                                  </Badge>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-sm text-slate-400 dark:text-slate-400">
                                    {data.confidence}% confidence
                                  </span>
                                  {data.is_significant ? (
                                    <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                                      <Check className="w-3 h-3 mr-1" />
                                      Significant
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-slate-400">
                                      Not Significant
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Recommendation */}
                      {results.recommendation && (
                        <div className="p-4 rounded-xl bg-emerald-50 bg-gradient-to-r from-emerald-50 to-indigo-50 dark:from-emerald-900/20 dark:to-indigo-900/20 border border-emerald-200 dark:border-cyan-800">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-cyan-900/30 flex items-center justify-center">
                              <Zap className="w-5 h-5 text-emerald-600 dark:text-cyan-400" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-cyan-900 dark:text-cyan-300">
                                Recommendation
                              </h4>
                              <p className="text-sm text-emerald-700 dark:text-cyan-400 mt-1">
                                {results.recommendation}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-400">
                      <FlaskConical className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No results data available</p>
                      <p className="text-sm mt-1">Start the experiment to begin collecting data</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>

      {/* How A/B Testing Works */}
      <Card data-testid="ab-testing-guide">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-slate-400" />
            How A/B Testing Works
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
              <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-cyan-900/30 flex items-center justify-center mb-3">
                <span className="font-bold text-emerald-600">1</span>
              </div>
              <h4 className="font-medium text-slate-900 dark:text-white mb-1">Create Variants</h4>
              <p className="text-sm text-slate-400 dark:text-slate-400">
                Define different versions of your content to test
              </p>
            </div>
            <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
              <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-cyan-900/30 flex items-center justify-center mb-3">
                <span className="font-bold text-emerald-600">2</span>
              </div>
              <h4 className="font-medium text-slate-900 dark:text-white mb-1">Split Traffic</h4>
              <p className="text-sm text-slate-400 dark:text-slate-400">
                Visitors are randomly assigned to each variant
              </p>
            </div>
            <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
              <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-cyan-900/30 flex items-center justify-center mb-3">
                <span className="font-bold text-emerald-600">3</span>
              </div>
              <h4 className="font-medium text-slate-900 dark:text-white mb-1">Collect Data</h4>
              <p className="text-sm text-slate-400 dark:text-slate-400">
                Track impressions, clicks, and conversions
              </p>
            </div>
            <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-800">
              <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-3">
                <span className="font-bold text-emerald-600">4</span>
              </div>
              <h4 className="font-medium text-slate-900 dark:text-white mb-1">Pick Winner</h4>
              <p className="text-sm text-slate-400 dark:text-slate-400">
                Deploy the variant with best conversion rate
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ABTesting;
